/**
 * Per-player match status for the public share page's roster panels
 * (PMA-COCK-125).
 *
 * The client's ask, in their own words: "de hecho que se le puede poner
 * colores, igual a los cambios, verdes y en ese caso pondria amarillos o
 * rojos" — colour the roster the way the substitutions already read
 * (green), and use yellow/red for cards. This module turns the two
 * already-derived public event lists into a per-player lookup the roster
 * rows render from; it does not touch raw `MatchEvent`s.
 *
 * Deliberately built on top of `derivePublicTeamCards` /
 * `derivePublicTeamSubstitutions` rather than re-reducing the raw event
 * stream: card standing is not a naive count. A `Card/"Cancelled"` event
 * revokes a booking, and the cockpit's second-yellow auto-pair emits a
 * `Yellow (Second)` immediately followed by a `Red` for what is one
 * dismissal. Both rules already live in `publicHeaderEvents.ts` (and are
 * unit-tested there); replaying them here would give the roster a second,
 * silently-diverging opinion about whether a player is still on the pitch.
 * That file gained `subjectId` / `offId` / `onId` for exactly this reuse.
 *
 * `minuteLabel` is resolved through a caller-supplied formatter rather than
 * parsed here, so the minute on a roster row is produced by the very same
 * `resolvePublicEventTiming` the timeline below it uses (stoppage-time and
 * halftime-break reconstruction included). A substitution shown as 67' in
 * the timeline and 66' in the roster is a bug report waiting to happen.
 *
 * `goals` (PMA-COCK-145/148, dated reversal 2026-09-24 — client wave, "The
 * Shot Flow Still Leaves the Pitch...") follows the identical rule for a
 * third source: `useLiveScore.ts`'s `goalEvents.home`/`.away`, the same
 * "what counts as a goal" (`type === "Shot" && data.outcome === "Goal"`)
 * list the public score bar and timeline already render from. This module
 * never re-filters raw `MatchEvent`s for goals any more than it does for
 * cards — a shootout kick is structurally invisible here for free (it is
 * stored as `type: "SetPiece"`, so it never enters `useLiveScore`'s
 * `Shot`-only filter in the first place).
 *
 * **Reversal (2026-09-24).** PMA-COCK-145/148 originally rendered a ball
 * count ("×N") and excluded own goals from the scorer's own tally entirely.
 * The client then asked for one ball PER goal (no multiplier) and for an
 * own goal to show on the scorer's OWN row ("como cualquier otro gol"). So:
 * `goalsScored`/`penaltyGoalsScored` (counts) are replaced by `goals`, an
 * ORDERED list of per-goal marks (`PlayerGoalMark`), one entry per ball, in
 * the order scored — the caller (`PublicTeamSheet.tsx`) renders one glyph
 * per entry instead of a glyph-plus-count. An own goal is no longer
 * excluded; it is tracked separately as `ownGoals` (a count, not a ball —
 * the client's "AG" mark is a small chip, not another ball in the row) and
 * attributed to the SCORER'S OWN roster by `player_id`/roster membership,
 * never by which side's `goalEvents` array the caller happened to pass in
 * (`useActionFlow.ts`'s OwnGoal handler reassigns `team_id` to the
 * BENEFITING team so the scoreboard is right, which means an own goal only
 * ever appears in the OPPONENT's `goalEvents` slice — the caller must now
 * pass the FULL combined goal list to both sides' `buildRosterStatusIndex`
 * call so each side can still find its own player's entry; see
 * `PublicMatchAnalytics.tsx`'s `homeRosterStatus`/`awayRosterStatus` wiring).
 */
import type {
  PublicCardEntry,
  PublicSubstitutionEntry,
} from "../../pages/logger/utils/publicHeaderEvents";
import type { PublicTimedEvent } from "./PublicMatchTimeline";
import { isPenaltyGoal } from "../../pages/logger/utils/penaltyGoal";

export interface PlayerMatchStatus {
  /** Minute label the player entered the match; null if they never came on. */
  cameOnMinute: string | null;
  /** Minute label the player left the match; null if they never came off. */
  wentOffMinute: string | null;
  /**
   * Yellow cards still standing (0-2). A dismissal by two yellows keeps
   * both — the roster renders two yellow chips plus the red, matching how
   * the timeline renders that same merged row.
   */
  yellowCards: number;
  /** True for a straight red or a second-yellow dismissal. */
  sentOff: boolean;
  /** Minute label of the dismissal; null when `sentOff` is false. */
  sentOffMinute: string | null;
  /**
   * Goals credited to the player's OWN team, one entry per ball, in the
   * order scored (PMA-COCK-145/148 dated reversal, 2026-09-24 — replaces
   * the old `goalsScored`/`penaltyGoalsScored` *counts* the client asked to
   * drop: "no mas del x2, solo mostrar la bola por cada gol"). Own goals
   * are NOT in this list — see `ownGoals` below. Stable across
   * substitutions: keyed by `player_id`, same as the cockpit's
   * `useGoalTally`.
   */
  goals: PlayerGoalMark[];
  /**
   * Own goals this player put into their own net (PMA-COCK-145/148 dated
   * reversal, 2026-09-24): now shown on the SCORER'S OWN row as an "AG"
   * chip, attributed by roster membership rather than by which side's
   * `goalEvents` array the event happens to sit in (`useActionFlow.ts`'s
   * OwnGoal handler reassigns `team_id` to the opponent so the scoreboard
   * is right; that reassignment must not hide the mark from the scorer's
   * own team sheet — see `buildRosterStatusIndex` below).
   */
  ownGoals: number;
}

/**
 * One ball the roster row renders. `kind` distinguishes a penalty goal (a
 * "(P)" mark rides alongside that specific ball) from a regular one — see
 * `PlayerMatchStatus.goals`'s doc comment for why this replaced a pair of
 * aggregate counts.
 */
export type PlayerGoalKind = "regular" | "penalty";

export interface PlayerGoalMark {
  kind: PlayerGoalKind;
}

/**
 * Minimal shape this module needs from a goal event — deliberately not the
 * full `MatchEvent` (this module has no other dependency on the raw event
 * type). Satisfied as-is by `useLiveScore.ts`'s `goalEvents.home`/`.away`.
 * `type`/`shot_type` are optional so every pre-existing caller/test that
 * predates PMA-COCK-195 keeps compiling — `isPenaltyGoal` treats a missing
 * `type` as `"Shot"`, matching the real shape `goalEvents` always carries.
 */
export interface PublicGoalEvent {
  player_id?: string | null;
  type?: string;
  data?: { own_goal?: boolean; shot_type?: string; outcome?: string } | null;
}

export type PublicRosterStatusIndex = ReadonlyMap<string, PlayerMatchStatus>;

/** Minute formatter — see the module doc for why this is injected. */
export type MinuteLabelResolver = (event: PublicTimedEvent) => string;

const blankStatus = (): PlayerMatchStatus => ({
  cameOnMinute: null,
  wentOffMinute: null,
  yellowCards: 0,
  sentOff: false,
  sentOffMinute: null,
  goals: [],
  ownGoals: 0,
});

/**
 * Build the per-player status lookup for ONE team.
 *
 * Substitutions are applied in list order (the derivation preserves event
 * order), so a player who comes on and is later taken off keeps the most
 * recent minute for each direction rather than the first — the tactical
 * double-swap case, rare but real.
 *
 * `goals` defaults to `[]` so an existing caller that hasn't threaded it
 * yet keeps compiling and every player simply shows zero goals — additive,
 * backward-compatible, matching PMA-COCK-145's identical `goalCountByPlayerId`
 * prop convention on the cockpit side.
 *
 * `goals` (the PARAMETER — every candidate goal event passed in, both
 * teams') must be the FULL combined list, not one side's own
 * `useLiveScore.ts`-attributed slice, for the own-goal reversal below to
 * work: see `PublicMatchAnalytics.tsx`'s `allGoalEvents` wiring.
 */
export function buildRosterStatusIndex(
  cards: PublicCardEntry[],
  substitutions: PublicSubstitutionEntry[],
  resolveMinuteLabel: MinuteLabelResolver,
  goals: PublicGoalEvent[] = [],
): PublicRosterStatusIndex {
  const index = new Map<string, PlayerMatchStatus>();

  const statusFor = (id: string): PlayerMatchStatus => {
    const existing = index.get(id);
    if (existing) return existing;
    const created = blankStatus();
    index.set(id, created);
    return created;
  };

  for (const sub of substitutions) {
    const minute = resolveMinuteLabel({
      timestamp: sub.timestamp,
      period: sub.period,
      match_clock: sub.minute,
    });
    if (sub.offId) statusFor(sub.offId).wentOffMinute = minute;
    if (sub.onId) statusFor(sub.onId).cameOnMinute = minute;
  }

  for (const card of cards) {
    if (!card.subjectId) continue;
    const status = statusFor(card.subjectId);
    const minute = resolveMinuteLabel({
      timestamp: card.timestamp,
      period: card.period,
      match_clock: card.minute,
    });
    if (card.kind === "yellow") {
      status.yellowCards += 1;
    } else if (card.kind === "red") {
      status.sentOff = true;
      status.sentOffMinute = minute;
    } else {
      // Merged second-yellow row: one dismissal, two standing yellows.
      status.yellowCards += 2;
      status.sentOff = true;
      status.sentOffMinute = minute;
    }
  }

  for (const goalEvent of goals) {
    if (!goalEvent.player_id) continue;
    const status = statusFor(goalEvent.player_id);
    // PMA-COCK-145/148 dated reversal (2026-09-24): an own goal now shows on
    // the SCORER'S OWN row, as a separate "AG" chip — not a ball in `goals`
    // (own goals were never "scored for the team" the way a real goal was,
    // so they don't belong in the same ordered ball list). Attributed here
    // purely by `player_id`, never by which side's `goals` array this event
    // arrived in — `useLiveScore.ts` buckets an own goal into the
    // BENEFITING team's slice by `team_id`, so the caller must pass the
    // full combined list to both sides for this to reach the true scorer's
    // own status entry (see this function's own doc comment).
    if (goalEvent.data?.own_goal === true) {
      status.ownGoals += 1;
      continue;
    }
    // PMA-COCK-195: `type` defaults to "Shot" — every real caller (goals
    // fed from `useLiveScore.ts`'s `goalEvents`) already carries it; the
    // default only covers a pre-existing test/caller shape that predates
    // this field (see `PublicGoalEvent`'s doc comment).
    const kind: PlayerGoalKind = isPenaltyGoal({
      type: goalEvent.type ?? "Shot",
      data: goalEvent.data,
    })
      ? "penalty"
      : "regular";
    status.goals.push({ kind });
  }

  return index;
}

/**
 * The single visual state a roster row renders in. Derived here rather than
 * in JSX so the precedence is stated once and testable:
 *
 * `sentOff` outranks everything — a dismissed player is off the pitch and
 * that is the most consequential thing about them, even if they also came
 * on as a substitute. Otherwise being taken off outranks having come on
 * (a substitute who was himself substituted is, right now, off), and
 * coming on outranks the default.
 */
export type RosterRowState = "sentOff" | "off" | "on" | "default";

export function resolveRosterRowState(
  status: PlayerMatchStatus | undefined,
): RosterRowState {
  if (!status) return "default";
  if (status.sentOff) return "sentOff";
  if (status.wentOffMinute) return "off";
  if (status.cameOnMinute) return "on";
  return "default";
}

/**
 * Whether a player is on the pitch RIGHT NOW, for the purpose of which
 * column of the team sheet they belong in.
 *
 * The client's ask: "muy dificil que cuando uno haga cambios los jugadores se
 * muevan de un lado a otro aqui?" — when a substitution is made, the two
 * players should trade columns, rather than the sheet staying frozen on the
 * kickoff eleven. So the split is driven by substitution history, with
 * `is_starter` only as the starting position:
 *
 *   substituted off  -> off the pitch (most recent fact wins)
 *   came on          -> on the pitch
 *   neither          -> wherever they kicked off
 *
 * A dismissal deliberately does NOT move anyone. A sent-off player has no
 * bench to return to, and listing them among the substitutes would read as
 * "available". Their row already says what happened — struck through, red
 * chip, `sentOff` row state — and it says it in the column where the viewer
 * last saw them playing. Note the consequence: the on-pitch column's count
 * still includes a dismissed player, so it is "the eleven that took the
 * field", not a live headcount.
 */
export function isOnPitch(
  status: PlayerMatchStatus | undefined,
  isStarter: boolean,
): boolean {
  if (status?.wentOffMinute) return false;
  if (status?.cameOnMinute) return true;
  return isStarter;
}
